import './index.scss';

import type React from 'react';

import { useCopy } from '../hooks';
import { CheckSVG, CopySVG } from '../svgs';

export interface ICopyValueProps {
	/** The text placed on the clipboard. */
	value: string | number | null | undefined;
	/** Human name of what is copied — drives the aria-label + tooltip, e.g. 'code', 'email'. */
	label?: string;
	/** Optional visible content shown before the icon (used with the 'inline' variant). */
	display?: React.ReactNode;
	/** 'icon' = button is just the copy glyph; 'inline' = shows `display` (or the value) with a trailing glyph. */
	variant?: 'inline' | 'icon';
	/** Icon size in px (default 16, matching the existing quick-analytics copy buttons). */
	size?: number;
	/** Stop the click from bubbling to a clickable parent (cards, dropdown items). */
	stopPropagation?: boolean;
	className?: string;
}

/**
 * Self-contained copy-to-clipboard control. Owns its own `useCopy` instance, so it can be
 * dropped in anywhere without threading copy state through parents. Renders nothing when the
 * value is empty (except the `display` node for the inline variant).
 */
export const CopyValue = ({
	value,
	label,
	display,
	variant = 'icon',
	size = 16,
	stopPropagation,
	className,
}: ICopyValueProps) => {
	const { copySuccess, copy } = useCopy();

	const text = value == null ? '' : String(value).trim();
	const hasValue = text !== '';

	// No value: render the visible text alone (inline) or nothing (icon-only).
	if (!hasValue) {
		return variant === 'inline' && display != null ? <>{display}</> : null;
	}

	const onCopy = (e: React.MouseEvent<HTMLButtonElement>) => {
		if (stopPropagation) e.stopPropagation();
		copy(text);
	};

	const classes = ['copy-btn', 'tool-tip', `copy-btn--${variant}`];
	if (copySuccess) classes.push('copy-btn--done');
	if (className) classes.push(className);

	return (
		<button
			type='button'
			className={classes.join(' ')}
			data-tooltip={copySuccess ? 'Copied' : 'Copy'}
			aria-label={`Copy ${label ?? 'value'}`}
			onClick={onCopy}
		>
			{variant === 'inline' && <span className='copy-btn__value'>{display ?? text}</span>}
			<span
				className='copy-btn__icon'
				aria-hidden='true'
			>
				{copySuccess ? (
					<CheckSVG
						width={`${size}`}
						height={`${size}`}
						color='currentColor'
					/>
				) : (
					<CopySVG
						width={`${size}`}
						height={`${size}`}
						color='currentColor'
					/>
				)}
			</span>
			<span
				className='copy-btn__sr'
				aria-live='polite'
			>
				{copySuccess ? 'Copied' : ''}
			</span>
		</button>
	);
};

export default CopyValue;
